import type { Product } from "../types/booking";

export type ProductDuplicateMatchBy = "ean" | "name";

export interface ProductDuplicateGroup {
  key: string;
  matchBy: ProductDuplicateMatchBy;
  matchValue: string;
  products: Product[];
}

export function getProductDedupKey(product: Product): {
  key: string;
  matchBy: ProductDuplicateMatchBy;
  matchValue: string;
} | null {
  const ean = product.ean != null ? String(product.ean).trim() : "";
  if (ean) {
    return { key: `ean:${ean}`, matchBy: "ean", matchValue: ean };
  }

  const name = (product.name || "").trim().toLowerCase();
  if (name) {
    return { key: `name:${name}`, matchBy: "name", matchValue: product.name.trim() };
  }

  return null;
}

export function findDuplicateProductGroups(
  products: Product[]
): ProductDuplicateGroup[] {
  const groups = new Map<string, ProductDuplicateGroup>();

  for (const product of products) {
    const dedupKey = getProductDedupKey(product);
    if (!dedupKey) continue;

    const existing = groups.get(dedupKey.key);
    if (existing) {
      existing.products.push(product);
      continue;
    }

    groups.set(dedupKey.key, {
      key: dedupKey.key,
      matchBy: dedupKey.matchBy,
      matchValue: dedupKey.matchValue,
      products: [product],
    });
  }

  return Array.from(groups.values())
    .filter((group) => group.products.length > 1)
    .sort((a, b) => b.products.length - a.products.length);
}

export function getDuplicateProductIds(
  products: Product[],
  keep: "oldest" | "newest" = "oldest"
): number[] {
  const groups = findDuplicateProductGroups(products);
  const idsToRemove: number[] = [];

  for (const group of groups) {
    const sorted = [...group.products].sort((a, b) =>
      keep === "oldest" ? a.id - b.id : b.id - a.id
    );
    idsToRemove.push(...sorted.slice(1).map((product) => product.id));
  }

  return idsToRemove;
}

export function getDuplicateProductIdSet(products: Product[]): Set<number> {
  return new Set(getDuplicateProductIds(products));
}
